

import DoctorComponent from '@/components/doctor/doctorComponent.vue';
import DoctorSchedule from '@/components/search/doctorSchedule.vue';
import CloseButton from '@/components/buttons/closeButton.vue';
import GoogleMaps from '@/components/doctor/googleMaps.vue';
import MapSearchButton from '@/components/buttons/MapSearchButton.vue';
import { getDoctorKey, resolveSelectedBookingKey } from '@/helpers/doctorSelectionHelper';

export default {
  name: "MapModal",
  components: {
    DoctorComponent,
    DoctorSchedule,
    CloseButton,
    GoogleMaps,
    MapSearchButton
  },

  props: {
    doctors: {
      type: Array,
      required: true,
    },
    mapOnly: {
      type: Boolean,
      default: false,
    },
    datesLoading: {
      type: Boolean,
      default: false,
    },
    searchLoading: {
      type: Boolean,
      default: false,
    },
    allowMapSearch: {
      type: Boolean,
      default: false,
    },
    selectedBookingKeys: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close', 'select-booking-option', 'request-calendar', 'request-more-dates', 'search-area'],
  data() {
    return {
      scrollLocked: false,
      mapReady: false,
      mapDirty: false,
      pendingMapState: null,
      lastSearchState: null,
    };
  },
  computed: {
    hasDoctors() {
      return Array.isArray(this.doctors) && this.doctors.length > 0;
    },
    showList() {
      return this.mapOnly !== true;
    },
    isSingleDoctor() {
      return Array.isArray(this.doctors) && this.doctors.length === 1;
    },
    mapPanelClass() {
      const base = 'map-modal__map-panel bg-white h-full relative border border-white w-full px-4 sm:px-5 py-5 sm:mt-0';
      if (this.showList) {
        return `${base} lg:w-1/2 rounded-b-[2.9rem] rounded-t-none md:rounded-[2.9rem] sm:border-t-0`;
      }
      return `${base} rounded-[2.9rem]`;
    },
  },
  mounted() {
    this.lockScroll();
    document.addEventListener('keydown', this.handleEsc);
    this.focusFirstDoctor();
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEsc);
    this.unlockScroll();
  },

  watch: {
    allowMapSearch(value) {
      if (!value) {
        this.mapDirty = false;
        this.pendingMapState = null;
        this.lastSearchState = null;
      }
    },
    doctors: {
      handler() {
        this.focusFirstDoctor();
      },
      immediate: true,
      deep: false,
    },
  },

  methods: {
    getDoctorKey(doctor) {
      return getDoctorKey(doctor);
    },
    resolveSelectedBookingKey(doctor) {
      return resolveSelectedBookingKey(doctor, this.selectedBookingKeys);
    },
    getDoctorScheduleKey(doctor, index) {
      const doctorKey = this.getDoctorKey(doctor);
      const baseKey = doctorKey || `doctor-${index}`;
      const bookingKey = this.resolveSelectedBookingKey(doctor) || 'default';
      return `${baseKey}::${bookingKey}`;
    },
    onDoctorViewMap(doctor) {
      if (this.$refs.map?.focusDoctor) {
        this.$refs.map.focusDoctor(doctor);
      }
    },
    focusFirstDoctor() {
      if (this.allowMapSearch && this.mapReady) {
        return;
      }
      const firstDoctor = Array.isArray(this.doctors) && this.doctors.length ? this.doctors[0] : null;
      if (!firstDoctor) {
        return;
      }
      this.$nextTick(() => {
        if (this.$refs.map?.focusDoctor) {
          this.$refs.map.focusDoctor(firstDoctor);
        }
      });
    },
    handleBookingOptionSelection(doctor, bookingKey, context) {
      if (bookingKey === undefined || bookingKey === null) {
        return;
      }
      this.$emit('select-booking-option', doctor, bookingKey, context);
    },
    handleScheduleBookingOption(doctor, bookingKey, context) {
      this.handleBookingOptionSelection(doctor, bookingKey, context);
    },
    handleRequestMoreDates(doctor, payload) {
      this.$emit('request-more-dates', doctor, payload);
    },
    handleRequestCalendar(doctor, payload) {
      this.$emit('request-calendar', doctor, payload);
    },

    handleMapIdle(state) {
      if (!this.allowMapSearch || !state) {
        return;
      }
      this.pendingMapState = state;
      if (!this.mapReady) {
        this.mapReady = true;
        this.lastSearchState = state;
        this.mapDirty = false;
        return;
      }
      this.mapDirty = !this.isSameMapState(state, this.lastSearchState);
    },
    requestSearchArea() {
      const mapState = this.pendingMapState || (this.$refs.map && this.$refs.map.getMapState ? this.$refs.map.getMapState() : null);
      if (!mapState || !mapState.center || !mapState.bounds) {
        return;
      }
      const radiusKm = this.calculateRadiusKm(mapState.center, mapState.bounds);
      if (!radiusKm) {
        return;
      }
      this.lastSearchState = mapState;
      this.mapDirty = false;
      this.$emit('search-area', {
        latitude: mapState.center.lat,
        longitude: mapState.center.lng,
        radiusKm,
      });
    },
    calculateRadiusKm(center, bounds) {
      if (!center || !bounds || !bounds.ne) {
        return null;
      }
      const distance = this.getDistanceKm(center, bounds.ne);
      if (!Number.isFinite(distance) || distance <= 0) {
        return null;
      }
      return distance;
    },
    getDistanceKm(pointA, pointB) {
      const toRadians = (value) => (value * Math.PI) / 180;
      const lat1 = toRadians(pointA.lat);
      const lat2 = toRadians(pointB.lat);
      const dLat = lat2 - lat1;
      const dLng = toRadians(pointB.lng) - toRadians(pointA.lng);
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
        + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return 6371 * c;
    },
    normalizeCoord(value) {
      if (!Number.isFinite(value)) {
        return null;
      }
      return Math.round(value * 10000);
    },
    isSameMapState(nextState, prevState) {
      if (!nextState || !prevState) {
        return false;
      }
      const nextCenter = nextState.center || null;
      const prevCenter = prevState.center || null;
      if (!nextCenter || !prevCenter) {
        return false;
      }
      const nextLat = this.normalizeCoord(nextCenter.lat);
      const nextLng = this.normalizeCoord(nextCenter.lng);
      const prevLat = this.normalizeCoord(prevCenter.lat);
      const prevLng = this.normalizeCoord(prevCenter.lng);
      if (nextLat === null || nextLng === null || prevLat === null || prevLng === null) {
        return false;
      }
      if (nextLat !== prevLat || nextLng !== prevLng) {
        return false;
      }
      const nextBounds = nextState.bounds || null;
      const prevBounds = prevState.bounds || null;
      if (!nextBounds || !prevBounds || !nextBounds.ne || !prevBounds.ne || !nextBounds.sw || !prevBounds.sw) {
        return false;
      }
      const nextNeLat = this.normalizeCoord(nextBounds.ne.lat);
      const nextNeLng = this.normalizeCoord(nextBounds.ne.lng);
      const prevNeLat = this.normalizeCoord(prevBounds.ne.lat);
      const prevNeLng = this.normalizeCoord(prevBounds.ne.lng);
      const nextSwLat = this.normalizeCoord(nextBounds.sw.lat);
      const nextSwLng = this.normalizeCoord(nextBounds.sw.lng);
      const prevSwLat = this.normalizeCoord(prevBounds.sw.lat);
      const prevSwLng = this.normalizeCoord(prevBounds.sw.lng);
      return nextNeLat === prevNeLat
        && nextNeLng === prevNeLng
        && nextSwLat === prevSwLat
        && nextSwLng === prevSwLng;
    },

    closeMapModal() {
      this.unlockScroll();
      this.$emit('close');
    },

    handleEsc(event) {
      if (event?.key === 'Escape') {
        this.closeMapModal();
      }
    },

    lockScroll() {
      if (this.scrollLocked) {
        return;
      }
      document.body.classList.add('overflow-hidden');
      this.scrollLocked = true;
    },

    unlockScroll() {
      if (!this.scrollLocked) {
        return;
      }
      document.body.classList.remove('overflow-hidden');
      this.scrollLocked = false;
    },
  },

};
